import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { StatusBadge } from "components/common/StatusBadge";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getPlanDetails } from "store/slices/membershipSlice";
import { BILLING_CYCLE, PLAN_OPTIONS } from "utils/constants";
import { CurrencySymbol, formatUTCDate } from "utils/utils";
import PageHeader from "components/common/PageHeader";

const PlanDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("membership_plan");

  const { loading, planDetails } = useSelector((state) => state.Membership);

  useEffect(() => {
    if (id && canView) {
      dispatch(getPlanDetails(id));
    }
  }, [id, canView]);

  const {
    title,
    planType,
    subTitle,
    description,
    pricingOptions,
    addonEnabled,
    addonIncluded,
    addonsAllowed,
    status,
    createdAt,
    iconUrl,
  } = planDetails;

  if (loading) {
    return (
      <div className="page-content">
        <PageHeader
          title={t("Plan Details")}
          isBackBtn
          className="details_page_title"
        />
        <div className="common_card">
          <DetailsShimmer />
        </div>
      </div>
    );
  }

  if (!canView) return null;

  return (
    <div className="page-content common_details_page">
      <PageHeader
        title={t("Plan Details")}
        isBackBtn
        className="details_page_title"
      />
      <div className="common_card">
        <Row className="details_page_item_row">
          <Col xs={6} lg={3} md={3}>
            <h6 className="label">{t("Plan Name")}</h6>
            <h6 className="title">{title?.en}</h6>
            <h6 className="value">{subTitle?.en}</h6>
          </Col>
          <Col xs={6} lg={3} md={3}>
            <h6 className="label">{t("Plan Type")}</h6>
            <h6 className="value">
              {PLAN_OPTIONS?.find((option) => option.value === planType)
                ?.name
                ? t(
                    PLAN_OPTIONS.find((option) => option.value === planType)
                      .name,
                  )
                : "-"}
            </h6>
          </Col>
          <Col xs={6} lg={3} md={3}>
            <h6 className="label">{t("Created Date")}</h6>
            <h6 className="value">{formatUTCDate(createdAt)}</h6>
          </Col>
          <Col xs={6} lg={3} md={3}>
            <h6 className="label">{t("Status")}</h6>
            <StatusBadge status={status} t={t} />
          </Col>
          {iconUrl && (
            <Col>
              <h6 className="label">{t("Icon")}</h6>
              <img
                src={iconUrl}
                className="mt-2"
                alt=""
                width={30}
                height={30}
              />
            </Col>
          )}
        </Row>
        <Row className="details_item_row">
          <h2 className="title_20">{t("Pricing")}</h2>
          <Col lg={12}>
            {pricingOptions?.map((item) => (
              <Row className="details_page_item_row">
                <Col lg={3} xs={6}>
                  <h6 className="label">{t("Pass")}</h6>
                  <h6 className="value">{item?.label}</h6>
                </Col>
                <Col lg={3} xs={6}>
                  <h6 className="label">{t("Billing Cycle")}</h6>
                  <h6 className="value">
                    {
                      BILLING_CYCLE?.find(
                        (option) => option?.value === item?.billingCycle,
                      )?.name &&
                      t(
                        BILLING_CYCLE.find(
                          (option) => option?.value === item?.billingCycle,
                        ).name,
                      )
                    }
                  </h6>
                </Col>
                <Col lg={3} xs={6}>
                  <h6 className="label">{t("Price")}</h6>
                  <h6 className="value">
                    <p className="price_symbol">
                      <CurrencySymbol width="12" height="14" />
                      {item?.price}
                    </p>
                  </h6>
                </Col>
                <Col lg={3} xs={6}>
                  <h6 className="label">{t("Popular")}</h6>
                  <h6 className="value">
                    {item?.isPopular ? t("Yes") : t("No")}
                  </h6>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>
        <Row className="details_item_row">
          <h2 className="title_20">{t("Addons")}</h2>
          <Col lg={12}>
            <Row className="details_page_item_row">
              <Col lg={3} xs={6}>
                <h6 className="label">{t("Addon Enabled")}</h6>
                <h6 className="value">{addonEnabled ? t("Yes") : t("No")}</h6>
              </Col>
              <Col lg={3} xs={6}>
                <h6 className="label">{t("Addon Included")}</h6>
                <h6 className="value">{addonIncluded ? t("Yes") : t("No")}</h6>
              </Col>
              <Col lg={6} xs={6}>
                <h6 className="label">{t("Allowed Addons")}</h6>
                <h6 className="value">
                  {addonsAllowed?.map((item) => item?.title?.en).join(", ")}
                </h6>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="details_item_row">
          <Col lg={12}>
            <h6 className="label">{t("Description")}</h6>
            <h6 className="value">{description?.en || "-"}</h6>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PlanDetails;
